<template>
    <Modal v-show="isOpen" :isOpen="isOpen" :title="modalTitle" size="4xl" :show-footer-buttons="true" @update:isOpen="handleClose">
        <template #header>
            <div class="flex items-center gap-3">
                <Button @click="copyToClipboard" :disabled="copied" variant="outline" size="sm">
                    <component :is="copied ? Check : Copy" class="h-4 w-4 mr-2" />
                    {{ copied ? 'Copied!' : 'Copy raw message' }}
                </Button>

                <Button v-if="showAiExplain" @click="explain" :disabled="loading" variant="default" size="sm">
                    <img src="/icons/streamkap_glitter_white.svg" alt="AI" class="h-4 w-4 mr-2" />
                    Explain using Streamkap AI
                    <Loader2 v-if="loading" class="h-4 w-4 ml-2 animate-spin" />
                </Button>
            </div>
        </template>
        <!-- Original Log Message -->
        <div class="space-y-6">
            <div class="rounded-lg border overflow-hidden bg-card">
                <div v-if="logLevel" class="bg-muted px-4 py-2 border-b">
                    <span class="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{{ logLevel }}</span>
                </div>

                <!-- Message controls: type badge and search -->
                <div v-if="isLongMessage" class="bg-muted/50 px-4 py-2 border-b flex items-center gap-3">
                    <Badge v-if="detectedMessageType !== 'Text'" variant="outline" class="text-xs">
                        {{ detectedMessageType }}
                    </Badge>
                    <Search class="h-3 w-3 text-muted-foreground" />
                    <input
                        v-model="searchTerm"
                        placeholder="Search..."
                        class="w-32 px-2 py-1 text-xs border rounded bg-background"
                        aria-label="Search message text"
                        aria-describedby="search-results" />
                    <span id="search-results" class="text-muted-foreground text-xs min-w-20" aria-live="polite">
                        {{ searchTerm && searchMatches ? searchMatches : '' }}
                    </span>
                </div>

                <div class="bg-muted/30 p-6 overflow-auto max-h-80 text-foreground">
                    <!-- Enhanced message display based on type -->
                    <component :is="messageDisplayComponent" :message="processedMessage" :search-term="searchTerm" @toggle-section="toggleSection" />
                </div>
            </div>

            <!-- AI Summary -->
            <Card v-if="hasAiData" class="bg-card relative">
                <CardHeader class="pb-4">
                    <CardTitle class="flex items-center justify-center space-x-2">
                        <img src="/icons/streamkap_glitter.svg" alt="AI" class="h-6 w-6" />
                        <span class="text-lg font-semibold">Streamkap AI – Log Explained</span>
                    </CardTitle>
                </CardHeader>

                <CardContent class="max-h-80 overflow-y-auto">
                    <!-- Summary -->
                    <Collapsible v-if="summaryData.summary" v-model:open="summaryExpanded" class="mb-6">
                        <CollapsibleTrigger class="flex items-center justify-between w-full text-left hover:bg-accent/10 rounded p-2">
                            <div class="flex items-center space-x-2">
                                <Info class="h-4 w-4 text-primary" />
                                <h4 class="text-base font-bold text-foreground">Summary</h4>
                            </div>
                            <ChevronDown class="h-4 w-4 transition-transform duration-200" :class="{ 'transform rotate-180': !summaryExpanded }" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div class="mt-2 pl-6">
                                <div
                                    v-html="sanitizedSummary"
                                    class="prose prose-sm max-w-none text-muted-foreground [&>*]:text-sm [&>p]:leading-relaxed"></div>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>

                    <!-- Recommended Actions -->
                    <Collapsible v-if="summaryData.recommended_actions" v-model:open="actionsExpanded" class="mb-6">
                        <CollapsibleTrigger class="flex items-center justify-between w-full text-left hover:bg-accent/10 rounded p-2">
                            <div class="flex items-center space-x-2">
                                <CheckCircle class="h-4 w-4 text-green-600 dark:text-green-400" />
                                <h4 class="text-base font-bold text-foreground">Recommended Actions</h4>
                            </div>
                            <ChevronDown class="h-4 w-4 transition-transform duration-200" :class="{ 'transform rotate-180': !actionsExpanded }" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div class="mt-2 pl-6">
                                <div
                                    v-html="sanitizedRecommendedActions"
                                    class="prose prose-sm max-w-none text-muted-foreground [&>*]:text-sm [&>p]:leading-relaxed"></div>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>

                    <!-- Context -->
                    <Collapsible v-if="summaryData.context" v-model:open="contextExpanded" class="mb-6">
                        <CollapsibleTrigger class="flex items-center justify-between w-full text-left hover:bg-accent/10 rounded p-2">
                            <div class="flex items-center space-x-2">
                                <Lightbulb class="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                                <h4 class="text-base font-bold text-foreground">Context</h4>
                            </div>
                            <ChevronDown class="h-4 w-4 transition-transform duration-200" :class="{ 'transform rotate-180': !contextExpanded }" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div class="mt-2 pl-6">
                                <div
                                    v-html="sanitizedContext"
                                    class="prose prose-sm max-w-none text-muted-foreground [&>*]:text-sm [&>p]:leading-relaxed"></div>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                </CardContent>

                <CardFooter class="pt-4 border-t">
                    <p class="text-xs text-muted-foreground text-center w-full">Streamkap AI can make mistakes. Please verify the information.</p>
                </CardFooter>
            </Card>
        </div>

        <template #footer>
            <Button @click="handleClose">Close</Button>
        </template>
    </Modal>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { Copy, Check, Info, CheckCircle, Lightbulb, Loader2, ChevronDown, Search } from 'lucide-vue-next';
import Modal from './global/modals/Modal.vue';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import StackTraceDisplay from './dialogs/error-display/StackTraceDisplay.vue';
import JsonErrorDisplay from './dialogs/error-display/JsonErrorDisplay.vue';
import SimpleErrorDisplay from './dialogs/error-display/SimpleErrorDisplay.vue';
import { useLogsStore } from '../stores/logs.store';
import DOMPurify from 'dompurify';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    title: {
        type: String,
        default: 'Log Message'
    },
    message: { type: String, required: true },
    isError: { type: Boolean, default: false },
    showAiExplain: { type: Boolean, default: false },
    connectorName: { type: String, required: false },
    namespaceName: { type: String, required: false },
    logLevel: { type: String, required: false },
    timestamp: { type: String, required: false },
    logSection: { type: String, required: false },
    entityType: { type: String, required: false },
    entityId: { type: String, required: false },
    logUuid: { type: String, required: false },
    // Now an object with keys: summary, context, recommended_actions (HTML string)
    summary: {
        type: Object,
        default: () => ({ summary: '', context: '', recommended_actions: '' })
    }
});

const emit = defineEmits(['update:isOpen']);
const logsStore = useLogsStore();

const copied = ref(false);
const loading = ref(false);
const summaryData = ref({ summary: '', context: '', recommended_actions: '' });
const searchTerm = ref('');
const collapsedSections = ref(new Set());

// Collapsible states - all expanded by default
const summaryExpanded = ref(true);
const actionsExpanded = ref(true);
const contextExpanded = ref(true);

// Safely sanitize HTML content
const sanitizedSummary = computed(() => DOMPurify.sanitize(summaryData.value.summary || ''));
const sanitizedRecommendedActions = computed(() => DOMPurify.sanitize(summaryData.value.recommended_actions || ''));
const sanitizedContext = computed(() => DOMPurify.sanitize(summaryData.value.context || ''));

// Determine if we have any AI data to show
const hasAiData = computed(() => !!summaryData.value.context || !!summaryData.value.summary || !!summaryData.value.recommended_actions);

// Enhanced error type detection
const detectedMessageType = computed(() => {
    const message = props.message;

    if (isComplexError(message)) return 'Complex';
    if (isStackTrace(message)) return 'Stack Trace';
    if (isJsonMessage(message)) return 'JSON';
    if (isValidationError(message)) return 'Validation';
    if (isSqlError(message)) return 'SQL';

    return 'Text';
});

const isLongMessage = computed(() => {
    return props.message.length > 500 || props.message.split('\n').length > 10;
});

const searchMatches = computed(() => {
    if (!searchTerm.value) return null;
    const matches = (props.message.match(new RegExp(searchTerm.value, 'gi')) || []).length;
    return matches > 0 ? `${matches} match${matches !== 1 ? 'es' : ''}` : 'No matches';
});

// Process message for enhanced display
const processedMessage = computed(() => {
    const message = props.message;
    const type = detectedMessageType.value;

    return {
        raw: message,
        type,
        lines: message.split('\n'),
        searchTerm: searchTerm.value,
        collapsedSections: collapsedSections.value
    };
});

// Dynamic component selection based on message type
const messageDisplayComponent = computed(() => {
    switch (detectedMessageType.value) {
        case 'Stack Trace':
            return StackTraceDisplay;
        case 'Complex':
        case 'JSON':
        case 'Validation':
            return JsonErrorDisplay;
        default:
            return SimpleErrorDisplay;
    }
});

// Function to update summary data
const updateSummaryData = () => {
    summaryData.value = {
        summary: props.summary.summary || '',
        context: props.summary.context || '',
        recommended_actions: props.summary.recommended_actions || ''
    };
};

// Initialise from existing prop
onMounted(() => {
    updateSummaryData();
});

// Watch for prop changes
watch(
    () => props.summary,
    () => {
        updateSummaryData();
    },
    { deep: true, immediate: true }
);

const handleClose = (open) => {
    // Handle both boolean parameter and direct call
    const shouldClose = typeof open === 'boolean' ? !open : true;
    if (shouldClose) {
        emit('update:isOpen', false);
        copied.value = false;
        loading.value = false;
        searchTerm.value = '';
        collapsedSections.value = new Set();
    }
};

// Computed property for modal title
const modalTitle = computed(() => props.title);

async function copyToClipboard() {
    copied.value = true;
    await navigator.clipboard.writeText(props.message);
    setTimeout(() => {
        copied.value = false;
    }, 3000);
}

async function explain() {
    loading.value = true;
    // clear previous
    summaryData.value = { summary: '', context: '', recommended_actions: '' };

    try {
        // compute snippet length
        // Call your store action
        let charsToCut = 31;

        if (props.message.length < 31) {
            charsToCut = props.message.length;
        }

        if (props.message.length > 100) {
            charsToCut = 100;
        }

        if (props.message.length > 150) {
            charsToCut = 150;
        }

        if (props.message.length > 200) {
            charsToCut = 200;
        }

        const result = await logsStore.summarize({
            connector_name: props.connectorName,
            namespace_name: props.namespaceName,
            timestamp: props.timestamp,
            log_section: props.message.substring(0, charsToCut),
            entity_type: props.entityType,
            entity_id: props.entityId,
            log_uuid: props.logUuid
        });

        // assign returned object
        summaryData.value = {
            summary: result.summary || '',
            context: result.context || '',
            recommended_actions: result.recommended_actions || ''
        };
    } catch (err) {
        console.error('AI summary failed', err);
    } finally {
        loading.value = false;
    }
}

// Message type detection functions
function isComplexError(message) {
    // Detect complex error messages with mixed formats and structures
    const complexPatterns = [
        // Messages starting with a prefix followed by JSON arrays
        /^[^:]+:\s*\[.*".*\\n.*".*\]/s,

        // Any JSON array containing strings with error patterns
        /\[.*".*(?:at\s+|Exception|Error:|\.java:|\.scala:|\.py:|\.js:|\.ts:|\.go:|\.rs:|\.cpp:|\.c\b).*\\n.*".*\]/s,

        // Messages with failure keywords + JSON structure
        /(?:failed|error|exception|crashed|timeout|denied).*\[.*".*(?:ERROR|WARN|INFO|DEBUG|FATAL).*\\n.*".*\]/is,

        // Arrays containing logging output with timestamps
        /\[.*".*\d{2}:\d{2}:\d{2}.*(?:ERROR|Exception).*\\n.*".*\]/s,

        // Mixed format: prefix + array with stack traces
        /^.+?:\s*\[.*".*(?:Caused by|at\s+\w+\.|Exception in thread).*\\n.*".*\]/s,

        // Arrays with escaped newlines containing class/method references
        /\[.*".*\\n.*(?:at\s+[\w.]+\(|org\.|com\.|java\.|scala\.|Exception:|Error:).*".*\]/s,

        // Complex deployment/build error structures
        /(?:deploy|build|compilation|execution).*failed.*\[.*".*\\n.*".*\]/is,

        // Generic: any bracketed content with multiple quoted strings containing newlines and error indicators
        /\[(?:[^"]*"[^"]*\\n[^"]*(?:error|exception|fail|at\s+|caused by)[^"]*"[^"]*,?\s*){2,}\]/is,

        // Mixed content: structured data + unescaped content
        /^[^[]*\[.*".*\\n.*".*\].*[\n\r].*(?:at\s+|Exception|Error:|Caused by)/s,

        // Generic escaped content pattern
        /\[.*".*\\[nt].*(?:ERROR|WARN|Exception|at\s+|Caused by|\.java:|\.scala:|\.py:).*\\[nt].*".*\]/s
    ];

    // Also check for structural complexity indicators
    const structuralComplexity =
        // Contains both JSON array structure AND stack trace patterns
        message.includes('[') &&
        message.includes('"') &&
        message.includes('\\n') &&
        /at\s+[\w.]+\(|Exception:|Error:|Caused by:/i.test(message) &&
        // Has multiple error-like strings
        (message.match(/"[^"]*(?:ERROR|WARN|Exception|at\s+)[^"]*"/g) || []).length >= 2;

    return complexPatterns.some((pattern) => pattern.test(message)) || structuralComplexity;
}

function isStackTrace(message) {
    const stackTracePatterns = [
        /at .+\(.+:\d+:\d+\)/, // Node.js/V8 stack traces
        /\s+at .+\(.+\.\w+:\d+\)/, // General stack trace pattern
        /\w+Error: .+\n\s+at/, // Error with stack trace
        /File ".+", line \d+/, // Python-style stack traces
        /\tat .+\.\w+\(.+\.\w+:\d+\)/, // Java-style stack traces
        /\n\s{2,}at\s/ // Indented "at" lines
    ];
    return stackTracePatterns.some((pattern) => pattern.test(message));
}

function isJsonMessage(message) {
    try {
        JSON.parse(message);
        return true;
    } catch {
        return message.includes('{') && message.includes('}') && (message.includes('"') || message.includes("'"));
    }
}

function isValidationError(message) {
    const validationPatterns = [
        /validation error/i,
        /field .+ is required/i,
        /invalid .+ format/i,
        /must be .+ characters/i,
        /does not match pattern/i
    ];
    return validationPatterns.some((pattern) => pattern.test(message));
}

function isSqlError(message) {
    const sqlPatterns = [/sql/i, /database/i, /query/i, /table .+ doesn't exist/i, /column .+ not found/i, /syntax error/i];
    return sqlPatterns.some((pattern) => pattern.test(message));
}

const toggleSection = (sectionId) => {
    if (collapsedSections.value.has(sectionId)) {
        collapsedSections.value.delete(sectionId);
    } else {
        collapsedSections.value.add(sectionId);
    }
    // Trigger reactivity
    collapsedSections.value = new Set(collapsedSections.value);
};
</script>
